/**
 * SPOT THE CHANGE — the castle room kit.
 *
 * A drawn room with fifteen places for things: five on the walls, four on the
 * mantel, three on the side table and three on the floor. A scene says what
 * stands in each place (or nothing) and in which colour. Scenes are plain data,
 * so the core can generate them from the seed, and the drawing is plain shapes,
 * so the TV and the phones render exactly the same picture.
 */
export const ROOM_VIEW = { width: 1600, height: 900 };
export const ROOM_SLOTS = [
    { kind: "wall", x: 230, y: 270, scale: 1 },
    { kind: "wall", x: 520, y: 215, scale: 0.75 },
    { kind: "wall", x: 800, y: 142, scale: 0.7 },
    { kind: "wall", x: 1080, y: 215, scale: 0.75 },
    { kind: "wall", x: 1370, y: 270, scale: 1 },
    { kind: "small", x: 655, y: 372, scale: 1.05 },
    { kind: "small", x: 745, y: 372, scale: 1.05 },
    { kind: "small", x: 855, y: 372, scale: 1.05 },
    { kind: "small", x: 945, y: 372, scale: 1.05 },
    { kind: "small", x: 1225, y: 602, scale: 1.15 },
    { kind: "small", x: 1320, y: 602, scale: 1.15 },
    { kind: "small", x: 1415, y: 602, scale: 1.15 },
    { kind: "floor", x: 200, y: 830, scale: 1 },
    { kind: "floor", x: 470, y: 840, scale: 0.85 },
    { kind: "floor", x: 1090, y: 850, scale: 0.75 },
];
/** Where a tap counts for each place (in room coordinates). */
export function slotBox(slot) {
    const s = slot.scale;
    if (slot.kind === "wall")
        return { x: slot.x - 105 * s, y: slot.y - 125 * s, w: 210 * s, h: 250 * s };
    if (slot.kind === "small")
        return { x: slot.x - 45 * s, y: slot.y - 135 * s, w: 90 * s, h: 140 * s };
    return { x: slot.x - 130 * s, y: slot.y - 330 * s, w: 260 * s, h: 340 * s };
}
/** The colours things come in: strong and far apart, so a change is fair. */
export const ROOM_TINTS = ["#b3203a", "#2f5fb3", "#2f8f5a", "#d9a520", "#6a3fa0", "#efe6d4"];
export const ROOM_TINT_NAMES = ["red", "blue", "green", "gold", "purple", "white"];
export const ROOM_ITEMS = {
    portrait: { kind: "wall", name: "portrait", tinted: true },
    shield: { kind: "wall", name: "shield", tinted: true },
    clock: { kind: "wall", name: "clock", tinted: true },
    mirror: { kind: "wall", name: "mirror", tinted: true },
    banner: { kind: "wall", name: "banner", tinted: true },
    antlers: { kind: "wall", name: "antlers", tinted: false },
    map: { kind: "wall", name: "map", tinted: true },
    candle: { kind: "small", name: "candle", tinted: true },
    goblet: { kind: "small", name: "goblet", tinted: true },
    vase: { kind: "small", name: "vase", tinted: true },
    books: { kind: "small", name: "pile of books", tinted: true },
    skull: { kind: "small", name: "skull", tinted: false },
    hourglass: { kind: "small", name: "hourglass", tinted: true },
    bottle: { kind: "small", name: "bottle", tinted: true },
    apple: { kind: "small", name: "apple", tinted: true },
    crown: { kind: "small", name: "crown", tinted: true },
    key: { kind: "small", name: "key", tinted: false },
    teapot: { kind: "small", name: "teapot", tinted: true },
    armour: { kind: "floor", name: "suit of armour", tinted: true },
    chair: { kind: "floor", name: "chair", tinted: true },
    chest: { kind: "floor", name: "chest", tinted: true },
    barrel: { kind: "floor", name: "barrel", tinted: false },
    plant: { kind: "floor", name: "plant", tinted: true },
    globe: { kind: "floor", name: "globe", tinted: true },
};
export const ROOM_ITEM_IDS = Object.keys(ROOM_ITEMS);
/** How many changes a room hides, and how many circles the table may draw. */
export const SPOT_CHANGES = 5;
export const SPOT_EXTRA_MARKS = 2;
/** Describe a change in words, for the result. */
export function describeChange(before, after) {
    const name = (t) => ROOM_ITEMS[t.item].name;
    if (before && !after)
        return `The ${name(before)} vanished`;
    if (!before && after)
        return `${/^[aeiou]/.test(name(after)) ? "An" : "A"} ${name(after)} appeared`;
    if (before && after && before.item !== after.item)
        return `The ${name(before)} became ${/^[aeiou]/.test(name(after)) ? "an" : "a"} ${name(after)}`;
    if (before && after)
        return `The ${name(before)} turned ${ROOM_TINT_NAMES[after.tint] ?? "a new colour"}`;
    return "Nothing";
}
const el = (tag, attrs, children) => (children ? { tag, attrs, children } : { tag, attrs });
const GOLD = "#c8a35f";
const GOLD_DARK = "#8a6a33";
const WOOD = "#6b4a2c";
const WOOD_DARK = "#4a3322";
const INK = "#1c1a17";
const BONE = "#e8dcc0";
const IVORY = "#efe6d4";
/** Darken (negative) or lighten a hex colour. */
function shade(hex, amount) {
    const n = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
    const out = n.map((v) => Math.round(amount < 0 ? v * (1 + amount) : v + (255 - v) * amount));
    return `#${out.map((v) => Math.max(0, Math.min(255, v)).toString(16).padStart(2, "0")).join("")}`;
}
function drawItem(item, tintIndex) {
    const t = ROOM_TINTS[tintIndex] ?? ROOM_TINTS[0];
    const dark = shade(t, -0.35);
    switch (item) {
        // Wall things, centred on (0, 0).
        case "portrait":
            return [
                el("rect", { x: -85, y: -105, width: 170, height: 210, fill: GOLD, stroke: GOLD_DARK, "stroke-width": 6 }),
                el("rect", { x: -68, y: -88, width: 136, height: 176, fill: t }),
                el("circle", { cx: 0, cy: -25, r: 28, fill: INK, "fill-opacity": 0.6 }),
                el("path", { d: "M-50 88 Q-48 20 0 15 Q48 20 50 88 Z", fill: INK, "fill-opacity": 0.6 }),
            ];
        case "shield":
            return [
                el("path", { d: "M-75 -95 H75 V-10 Q75 70 0 105 Q-75 70 -75 -10 Z", fill: t, stroke: GOLD, "stroke-width": 7 }),
                el("path", { d: "M-62 15 L0 -40 L62 15 L62 45 L0 -10 L-62 45 Z", fill: tintIndex === 5 ? INK : IVORY }),
            ];
        case "clock": {
            const ticks = Array.from({ length: 12 }, (_, i) => {
                const a = (i * Math.PI) / 6;
                return el("line", { x1: Math.sin(a) * 52, y1: -Math.cos(a) * 52, x2: Math.sin(a) * 62, y2: -Math.cos(a) * 62, stroke: INK, "stroke-width": 4 });
            });
            return [
                el("circle", { cx: 0, cy: 0, r: 88, fill: t, stroke: GOLD, "stroke-width": 7 }),
                el("circle", { cx: 0, cy: 0, r: 68, fill: IVORY }),
                ...ticks,
                el("line", { x1: 0, y1: 0, x2: 0, y2: -48, stroke: INK, "stroke-width": 7, "stroke-linecap": "round" }),
                el("line", { x1: 0, y1: 0, x2: 34, y2: 10, stroke: INK, "stroke-width": 7, "stroke-linecap": "round" }),
                el("circle", { cx: 0, cy: 0, r: 7, fill: GOLD_DARK }),
            ];
        }
        case "mirror":
            return [
                el("ellipse", { cx: 0, cy: 0, rx: 72, ry: 105, fill: t, stroke: GOLD, "stroke-width": 5 }),
                el("ellipse", { cx: 0, cy: 0, rx: 56, ry: 88, fill: "#9fc3cf" }),
                el("path", { d: "M-30 -50 Q-38 0 -22 45", fill: "none", stroke: "#ffffff", "stroke-opacity": 0.6, "stroke-width": 8, "stroke-linecap": "round" }),
            ];
        case "banner":
            return [
                el("path", { d: "M-65 -98 H65 V95 L0 60 L-65 95 Z", fill: t, stroke: dark, "stroke-width": 4 }),
                el("circle", { cx: 0, cy: -15, r: 30, fill: GOLD }),
                el("path", { d: "M0 -35 L12 -8 L-14 -24 H14 L-12 -8 Z", fill: dark }),
                el("line", { x1: -85, y1: -100, x2: 85, y2: -100, stroke: GOLD, "stroke-width": 9, "stroke-linecap": "round" }),
            ];
        case "antlers":
            return [
                el("path", {
                    d: "M-10 10 Q-60 0 -80 -60 M-55 -20 L-80 -10 M-68 -45 L-95 -45 M10 10 Q60 0 80 -60 M55 -20 L80 -10 M68 -45 L95 -45",
                    fill: "none",
                    stroke: "#d9ccae",
                    "stroke-width": 11,
                    "stroke-linecap": "round",
                }),
                el("path", { d: "M-40 0 H40 Q40 70 0 95 Q-40 70 -40 0 Z", fill: WOOD, stroke: GOLD_DARK, "stroke-width": 4 }),
            ];
        case "map":
            return [
                el("rect", { x: -92, y: -72, width: 184, height: 144, fill: "#e8dcc0", stroke: GOLD_DARK, "stroke-width": 5 }),
                el("path", { d: "M-80 -60 H80 V60 H-80 Z", fill: t, "fill-opacity": 0.75 }),
                el("path", { d: "M-55 -40 Q-10 -55 10 -20 Q30 5 -5 30 Q-40 45 -55 10 Q-70 -15 -55 -40 Z M30 20 Q55 10 65 35 Q50 55 30 45 Z", fill: "#c9b98f", stroke: GOLD_DARK, "stroke-width": 2 }),
                el("path", { d: "M-20 -10 L0 0 M0 0 L-10 15", stroke: "#b3203a", "stroke-width": 4, "stroke-dasharray": "6 5", fill: "none" }),
            ];
        // Small things, standing on (0, 0).
        case "candle":
            return [
                el("circle", { cx: 0, cy: -108, r: 30, fill: "#f2b705", "fill-opacity": 0.18 }),
                el("rect", { x: -11, y: -92, width: 22, height: 88, fill: t, stroke: dark, "stroke-width": 2 }),
                el("path", { d: "M0 -127 Q15 -106 0 -94 Q-15 -106 0 -127 Z", fill: "#f2b705" }),
                el("ellipse", { cx: 0, cy: -4, rx: 30, ry: 8, fill: GOLD, stroke: GOLD_DARK, "stroke-width": 2 }),
            ];
        case "goblet":
            return [
                el("path", { d: "M-30 -112 H30 Q28 -70 0 -60 Q-28 -70 -30 -112 Z", fill: t, stroke: dark, "stroke-width": 3 }),
                el("rect", { x: -4, y: -62, width: 8, height: 50, fill: t }),
                el("ellipse", { cx: 0, cy: -8, rx: 25, ry: 8, fill: t, stroke: dark, "stroke-width": 2 }),
            ];
        case "vase":
            return [
                el("path", { d: "M-6 -95 Q-14 -125 -20 -132 M6 -95 Q10 -128 18 -136 M0 -95 V-140", stroke: "#3f8f3a", "stroke-width": 4, fill: "none" }),
                el("circle", { cx: -20, cy: -134, r: 9, fill: "#f2b705" }),
                el("circle", { cx: 18, cy: -138, r: 9, fill: "#e0475d" }),
                el("circle", { cx: 0, cy: -144, r: 9, fill: IVORY }),
                el("path", { d: "M-16 -96 Q-40 -60 -28 -20 Q-24 0 0 0 Q24 0 28 -20 Q40 -60 16 -96 Z", fill: t, stroke: dark, "stroke-width": 3 }),
            ];
        case "books":
            return [
                el("rect", { x: -40, y: -24, width: 80, height: 24, fill: WOOD, stroke: INK, "stroke-width": 2 }),
                el("rect", { x: -34, y: -48, width: 72, height: 24, fill: t, stroke: INK, "stroke-width": 2 }),
                el("rect", { x: -38, y: -70, width: 76, height: 22, fill: "#2f4f4f", stroke: INK, "stroke-width": 2 }),
                el("line", { x1: -26, y1: -36, x2: 26, y2: -36, stroke: GOLD, "stroke-width": 3 }),
            ];
        case "skull":
            return [
                el("path", { d: "M-32 -50 Q-34 -95 0 -96 Q34 -95 32 -50 Q30 -30 20 -26 V-6 H-20 V-26 Q-30 -30 -32 -50 Z", fill: BONE, stroke: "#9b8f73", "stroke-width": 3 }),
                el("circle", { cx: -13, cy: -55, r: 9, fill: INK }),
                el("circle", { cx: 13, cy: -55, r: 9, fill: INK }),
                el("path", { d: "M0 -42 L-5 -32 H5 Z", fill: INK }),
                el("path", { d: "M-12 -14 V-4 M-4 -14 V-4 M4 -14 V-4 M12 -14 V-4", stroke: "#9b8f73", "stroke-width": 2 }),
            ];
        case "hourglass":
            return [
                el("path", { d: "M-22 -100 H22 Q22 -70 3 -55 Q22 -40 22 -10 H-22 Q-22 -40 -3 -55 Q-22 -70 -22 -100 Z", fill: "#d9ccae", "fill-opacity": 0.55, stroke: "#d9ccae", "stroke-width": 2 }),
                el("path", { d: "M-18 -12 Q-14 -32 0 -36 Q14 -32 18 -12 Z M-12 -86 H12 Q8 -70 0 -62 Q-8 -70 -12 -86 Z", fill: "#c98a12" }),
                el("rect", { x: -30, y: -112, width: 60, height: 12, fill: t, stroke: dark, "stroke-width": 2 }),
                el("rect", { x: -30, y: -12, width: 60, height: 12, fill: t, stroke: dark, "stroke-width": 2 }),
            ];
        case "bottle":
            return [
                el("path", { d: "M-9 -118 H9 V-88 Q30 -78 30 -50 V-6 Q30 0 24 0 H-24 Q-30 0 -30 -6 V-50 Q-30 -78 -9 -88 Z", fill: t, stroke: dark, "stroke-width": 3 }),
                el("rect", { x: -8, y: -130, width: 16, height: 14, fill: WOOD }),
                el("rect", { x: -20, y: -50, width: 40, height: 26, fill: IVORY, stroke: dark, "stroke-width": 2 }),
            ];
        case "apple":
            return [
                el("path", { d: "M0 -56 Q-34 -66 -32 -32 Q-30 -2 -8 0 Q0 -4 8 0 Q30 -2 32 -32 Q34 -66 0 -56 Z", fill: t, stroke: dark, "stroke-width": 3 }),
                el("path", { d: "M0 -56 Q2 -70 8 -76", stroke: WOOD_DARK, "stroke-width": 4, fill: "none" }),
                el("path", { d: "M6 -68 Q22 -80 30 -68 Q18 -60 6 -68 Z", fill: "#3f8f3a" }),
            ];
        case "crown":
            return [
                el("path", { d: "M-40 -10 L-44 -70 L-22 -44 L0 -80 L22 -44 L44 -70 L40 -10 Z", fill: "#d9a520", stroke: GOLD_DARK, "stroke-width": 3 }),
                el("rect", { x: -40, y: -14, width: 80, height: 14, fill: "#d9a520", stroke: GOLD_DARK, "stroke-width": 3 }),
                el("circle", { cx: 0, cy: -34, r: 8, fill: tintIndex === 3 ? "#b3203a" : t }),
                el("circle", { cx: -24, cy: -7, r: 5, fill: tintIndex === 3 ? "#b3203a" : t }),
                el("circle", { cx: 24, cy: -7, r: 5, fill: tintIndex === 3 ? "#b3203a" : t }),
            ];
        case "key":
            return [
                el("circle", { cx: -28, cy: -14, r: 14, fill: "none", stroke: "#d9a520", "stroke-width": 7 }),
                el("path", { d: "M-14 -14 H38 M24 -14 V-2 M34 -14 V-4", stroke: "#d9a520", "stroke-width": 7, fill: "none", "stroke-linecap": "round" }),
            ];
        case "teapot":
            return [
                el("path", { d: "M30 -40 Q55 -50 58 -72", stroke: t, "stroke-width": 9, fill: "none", "stroke-linecap": "round" }),
                el("path", { d: "M-32 -60 Q-58 -60 -54 -36 Q-50 -20 -32 -26", stroke: t, "stroke-width": 7, fill: "none" }),
                el("path", { d: "M-36 -62 Q-40 0 0 0 Q40 0 36 -62 Z", fill: t, stroke: dark, "stroke-width": 3 }),
                el("path", { d: "M-26 -62 Q0 -84 26 -62 Z", fill: dark }),
                el("circle", { cx: 0, cy: -80, r: 6, fill: dark }),
            ];
        // Floor things, standing on (0, 0).
        case "armour":
            return [
                el("rect", { x: -38, y: -130, width: 28, height: 128, fill: "#8d969a", stroke: "#4e5559", "stroke-width": 3 }),
                el("rect", { x: 10, y: -130, width: 28, height: 128, fill: "#8d969a", stroke: "#4e5559", "stroke-width": 3 }),
                el("path", { d: "M-55 -240 H55 L45 -130 H-45 Z", fill: "#a7b0b4", stroke: "#4e5559", "stroke-width": 3 }),
                el("rect", { x: -78, y: -235, width: 22, height: 100, rx: 10, fill: "#8d969a", stroke: "#4e5559", "stroke-width": 3 }),
                el("rect", { x: 56, y: -235, width: 22, height: 100, rx: 10, fill: "#8d969a", stroke: "#4e5559", "stroke-width": 3 }),
                el("path", { d: "M-30 -250 Q-32 -305 0 -308 Q32 -305 30 -250 Z", fill: "#a7b0b4", stroke: "#4e5559", "stroke-width": 3 }),
                el("rect", { x: -20, y: -285, width: 40, height: 6, fill: INK }),
                el("path", { d: "M0 -306 Q30 -340 52 -318 Q30 -322 10 -300 Z", fill: t }),
            ];
        case "chair":
            return [
                el("rect", { x: -70, y: -310, width: 140, height: 190, rx: 10, fill: WOOD, stroke: WOOD_DARK, "stroke-width": 4 }),
                el("rect", { x: -54, y: -292, width: 108, height: 150, rx: 8, fill: t }),
                el("rect", { x: -80, y: -130, width: 160, height: 34, rx: 6, fill: t, stroke: dark, "stroke-width": 3 }),
                el("rect", { x: -74, y: -96, width: 14, height: 96, fill: WOOD_DARK }),
                el("rect", { x: 60, y: -96, width: 14, height: 96, fill: WOOD_DARK }),
            ];
        case "chest":
            return [
                el("rect", { x: -115, y: -120, width: 230, height: 120, fill: WOOD, stroke: WOOD_DARK, "stroke-width": 4 }),
                el("path", { d: "M-115 -120 Q-115 -185 0 -188 Q115 -185 115 -120 Z", fill: shade(WOOD, 0.1), stroke: WOOD_DARK, "stroke-width": 4 }),
                el("rect", { x: -85, y: -186, width: 22, height: 186, fill: t }),
                el("rect", { x: 63, y: -186, width: 22, height: 186, fill: t }),
                el("rect", { x: -16, y: -132, width: 32, height: 34, fill: "#d9a520", stroke: GOLD_DARK, "stroke-width": 3 }),
            ];
        case "barrel":
            return [
                el("path", { d: "M-80 -230 Q-100 -115 -80 0 H80 Q100 -115 80 -230 Z", fill: "#8a5a32", stroke: WOOD_DARK, "stroke-width": 4 }),
                el("path", { d: "M-40 -230 Q-48 -115 -40 0 M0 -230 V0 M40 -230 Q48 -115 40 0", stroke: WOOD_DARK, "stroke-width": 3, fill: "none" }),
                el("path", { d: "M-88 -190 H88 M-94 -40 H94", stroke: "#3b3f3e", "stroke-width": 12 }),
            ];
        case "plant":
            return [
                el("path", { d: "M0 -110 Q-60 -200 -95 -190 Q-60 -150 -8 -110 M0 -110 Q60 -230 100 -215 Q60 -160 8 -110 M0 -110 Q-20 -260 10 -300 Q20 -220 4 -110", fill: "#3f8f3a", stroke: "#24582a", "stroke-width": 3 }),
                el("path", { d: "M-60 -115 H60 L45 0 H-45 Z", fill: t, stroke: dark, "stroke-width": 4 }),
                el("rect", { x: -66, y: -126, width: 132, height: 16, fill: dark }),
            ];
        case "globe":
            return [
                el("path", { d: "M-50 0 L0 -60 L50 0 M0 -60 V-90", stroke: WOOD_DARK, "stroke-width": 10, fill: "none" }),
                el("circle", { cx: 0, cy: -175, r: 82, fill: t, stroke: dark, "stroke-width": 4 }),
                el("path", { d: "M-50 -220 Q-10 -240 10 -205 Q-5 -170 -40 -165 Q-70 -180 -50 -220 Z M20 -150 Q55 -160 60 -125 Q35 -110 20 -150 Z", fill: tintIndex === 2 ? "#d9ccae" : "#3f8f3a" }),
                el("path", { d: "M-95 -175 A95 95 0 0 1 95 -175", stroke: GOLD, "stroke-width": 7, fill: "none" }),
            ];
    }
}
/** Mortar lines, boards and the fixed furniture: the same in every scene. */
function backdrop() {
    const stones = [];
    for (let row = 0; row * 70 < 560; row++) {
        const y = row * 70;
        stones.push(`M0 ${y} H1600`);
        for (let x = (row % 2) * 80; x < 1600; x += 160)
            stones.push(`M${x} ${y} V${y + 70}`);
    }
    const boards = [];
    for (let y = 730; y < 900; y += 42)
        boards.push(`M0 ${y} H1600`);
    return [
        el("rect", { x: 0, y: 0, width: 1600, height: 700, fill: "#33403a" }),
        el("path", { d: stones.join(" "), stroke: "#24302b", "stroke-width": 4, fill: "none" }),
        el("rect", { x: 0, y: 560, width: 1600, height: 140, fill: "#3b2a1d" }),
        el("rect", { x: 0, y: 552, width: 1600, height: 14, fill: WOOD }),
        el("rect", { x: 0, y: 700, width: 1600, height: 200, fill: WOOD_DARK }),
        el("path", { d: boards.join(" "), stroke: "#382618", "stroke-width": 3, fill: "none" }),
        el("ellipse", { cx: 800, cy: 810, rx: 430, ry: 70, fill: "#6e1f2c", stroke: GOLD, "stroke-width": 5 }),
        el("ellipse", { cx: 800, cy: 810, rx: 370, ry: 50, fill: "none", stroke: GOLD, "stroke-width": 2, "stroke-dasharray": "14 10" }),
        // The fireplace and its mantel.
        el("rect", { x: 610, y: 385, width: 380, height: 315, fill: "#5d5852", stroke: "#3b3833", "stroke-width": 5 }),
        el("path", { d: "M660 700 V520 Q660 455 800 455 Q940 455 940 520 V700 Z", fill: "#120c0a" }),
        el("path", { d: "M735 700 Q700 640 750 590 Q745 640 770 650 Q760 590 805 545 Q800 610 830 625 Q835 590 860 580 Q900 650 865 700 Z", fill: "#e36a1c" }),
        el("path", { d: "M765 700 Q745 660 780 630 Q785 660 800 662 Q805 625 830 610 Q855 665 835 700 Z", fill: "#f2b705" }),
        el("rect", { x: 690, y: 690, width: 220, height: 14, fill: "#2a2522" }),
        el("rect", { x: 585, y: 372, width: 430, height: 24, fill: WOOD, stroke: WOOD_DARK, "stroke-width": 4 }),
        // The side table.
        el("rect", { x: 1170, y: 620, width: 16, height: 150, fill: WOOD_DARK }),
        el("rect", { x: 1454, y: 620, width: 16, height: 150, fill: WOOD_DARK }),
        el("rect", { x: 1150, y: 600, width: 340, height: 24, fill: WOOD, stroke: WOOD_DARK, "stroke-width": 4 }),
    ];
}
/** Every shape in the scene, ready to render as SVG in a 1600 × 900 view. */
export function drawRoom(scene) {
    const things = ROOM_SLOTS.map((slot, i) => {
        const thing = scene[i];
        if (!thing)
            return null;
        return el("g", { transform: `translate(${slot.x} ${slot.y}) scale(${slot.scale})` }, drawItem(thing.item, thing.tint));
    }).filter((s) => s !== null);
    return [...backdrop(), ...things];
}
//# sourceMappingURL=room.js.map